import React, { useState } from 'react';
import { formatLondon } from '../../utils/dateFormatting';
import { eventEnd } from '../../utils/schedule';
import ErrorBanner from './ErrorBanner';

const DEFAULT_TEMP = 18;
// How long to keep the building warm after the target time.
const DURATIONS = [
    { minutes: 0, label: 'Stop at target' },
    { minutes: 30, label: '30 min' },
    { minutes: 60, label: '1 hour' },
    { minutes: 90, label: '1.5 hours' },
    { minutes: 120, label: '2 hours' },
    { minutes: 180, label: '3 hours' },
    { minutes: 240, label: '4 hours' },
];

// Comfort objectives table and form. State and API calls live in
// useHeatingSchedule; this only renders. Every add/delete re-solves, so the
// forecast, planned actions and chart overlay stay in step with the scheduler.
const HeatingSchedule = ({ deviceId, schedule, canEdit = true }) => {
    const { events, loading, busy, error, addEvent, removeEvent, resolve } = schedule;
    const [targetTime, setTargetTime] = useState('');
    const [targetTemp, setTargetTemp] = useState(DEFAULT_TEMP);
    const [label, setLabel] = useState('');
    const [duration, setDuration] = useState(60);

    if (!deviceId) {
        return <p className="text-gray-500 text-center py-4">Select a device to manage the heating schedule</p>;
    }

    const handleAdd = async () => {
        if (!targetTime) return;
        const ok = await addEvent({ targetTime, targetTemperature: targetTemp, label, durationMinutes: duration });
        if (ok) {
            setTargetTime('');
            setTargetTemp(DEFAULT_TEMP);
            setLabel('');
        }
    };

    const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-rebel-green';

    return (
        <div>
            {canEdit && (
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-4">
                    <label className="block text-sm text-gray-600">
                        <span className="block mb-1">Target date and time</span>
                        <input type="datetime-local" value={targetTime} onChange={(e) => setTargetTime(e.target.value)} className={inputClass} />
                    </label>
                    <label className="block text-sm text-gray-600">
                        <span className="block mb-1">Target temperature</span>
                        <input type="number" value={targetTemp} onChange={(e) => setTargetTemp(e.target.value)} min="5" max="30" step="0.5" className={inputClass} />
                    </label>
                    <label className="block text-sm text-gray-600">
                        <span className="block mb-1">Keep warm for</span>
                        <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} className={inputClass}>
                            {DURATIONS.map(d => <option key={d.minutes} value={d.minutes}>{d.label}</option>)}
                        </select>
                    </label>
                    <label className="block text-sm text-gray-600">
                        <span className="block mb-1">Label (optional)</span>
                        <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Sunday Service" className={inputClass} />
                    </label>
                    <div className="flex items-end">
                        <button
                            onClick={handleAdd}
                            disabled={!!busy || !targetTime}
                            className="w-full bg-rebel-green hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 disabled:opacity-50"
                        >
                            {busy === 'saving' ? 'Calculating...' : 'Add event'}
                        </button>
                    </div>
                </div>
            )}

            <ErrorBanner message={error} className="mb-3" />

            {loading && events.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Loading...</p>
            ) : events.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No heating events scheduled</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-2 px-2 text-gray-600">Label</th>
                                <th className="text-left py-2 px-2 text-gray-600">Target time</th>
                                <th className="text-right py-2 px-2 text-gray-600">Target temp</th>
                                <th className="text-left py-2 px-2 text-gray-600">Warm until</th>
                                <th className="text-left py-2 px-2 text-gray-600">Pre-heat start</th>
                                {canEdit && <th className="text-right py-2 px-2 text-gray-600"><span className="sr-only">Actions</span></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((event) => (
                                <tr key={event.event_id} className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="py-2 px-2">{event.label || '-'}</td>
                                    <td className="py-2 px-2">{formatLondon(event.target_time, 'weekdayDateTime')}</td>
                                    <td className="py-2 px-2 text-right">{event.target_temperature}&deg;C</td>
                                    <td className="py-2 px-2">{event.duration_minutes ? formatLondon(eventEnd(event), 'time') : '-'}</td>
                                    <td className="py-2 px-2">
                                        {event.preheat_start ? (
                                            <span className="text-orange-600 font-medium">{formatLondon(event.preheat_start, 'weekdayDateTime')}</span>
                                        ) : '-'}
                                    </td>
                                    {canEdit && (
                                        <td className="py-2 px-2 text-right">
                                            <button
                                                onClick={() => removeEvent(event.event_id)}
                                                disabled={!!busy}
                                                className="text-red-500 hover:text-red-700 text-xs disabled:opacity-50"
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {canEdit && events.length > 0 && (
                <div className="mt-4">
                    <button
                        onClick={resolve}
                        disabled={!!busy}
                        className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 disabled:opacity-50"
                    >
                        {busy === 'solving' ? 'Updating...' : 'Re-solve'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default HeatingSchedule;
